import {ElementShapeType} from '../../../entities/project'
import {PolygonToolLayer} from '../toolLayers/PolygonToolLayer'
import {RectToolLayer} from '../toolLayers/RectToolLayer'
import {ToolLayer} from '../toolLayers/ToolLayer'
import {PolygonTransformerLayer} from '../transformerLayers/PolygonTransformerLayer'
import {RectTransformerLayer} from '../transformerLayers/RectTransformerLayer'
import {TransformerLayer} from '../transformerLayers/TransformerLayer'
import {PolygonTransformerUI} from '../transformerUis/PolygonTransformerUI'
import {RectTransformerUI} from '../transformerUis/RectTransformerUI'
import {TransformerUI} from '../transformerUis/TransformerUI'
import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import * as Icons from '@fortawesome/free-solid-svg-icons'
import React from 'react'
import {EllipseToolLayer} from '../toolLayers/EllipseToolLayer'
import {EllipseTransformerUI} from '../transformerUis/EllipseTransformerUI'
import {EllipseTransformerLayer} from '../transformerLayers/EllipseTransformerLayer'
import {PlantingToolLayer} from '../toolLayers/PlantingToolLayer'
import {ImageToolLayer} from '../toolLayers/ImageToolLayer'
import sproutIconUrl from 'url:/public/assets/cursors/sprout.png'
import {TypedElement} from '../../../entities/project'

export type CanvasElementsRegistryEntry = {
  toolLayer: ToolLayer
  transformerLayer: TransformerLayer
  transformerUI: TransformerUI
  toolIcon: React.ReactNode
  toolCursor?: string
  canvasShapeType: string
  getAabb?(
    element: TypedElement,
    pixelsPerCm: number
  ): {xFrom: number; yFrom: number; xTo: number; yTo: number}
}

const centerPointAabb: CanvasElementsRegistryEntry['getAabb'] = (element, pixelsPerCm) => {
  return {
    xFrom: element.props.x,
    xTo: element.props.x,
    yFrom: element.props.y,
    yTo: element.props.y,
  }
}

export type CanvasElementsRegistry = Record<ElementShapeType, CanvasElementsRegistryEntry>

export const canvasElementsRegistry: CanvasElementsRegistry = {
  polygon: {
    toolLayer: PolygonToolLayer,
    transformerLayer: PolygonTransformerLayer,
    transformerUI: PolygonTransformerUI,
    toolIcon: <Icon icon={Icons.faDrawPolygon} />,
    canvasShapeType: 'Line',
    getAabb(element, pixelsPerCm) {
      return {
        xFrom: Math.min(element.props.points),
        xTo: Math.max(element.props.points),
        yFrom: Math.min(element.props.points),
        yTo: Math.max(element.props.points),
      }
    },
  },
  rect: {
    toolLayer: RectToolLayer,
    transformerLayer: RectTransformerLayer,
    transformerUI: RectTransformerUI,
    toolIcon: <Icon icon={Icons.faSquare} />,
    canvasShapeType: 'Rect',
    getAabb(element, pixelsPerCm) {
      return {
        xFrom:
          element.props.x -
          (1 - Math.cos(element.props.rotaion ? element.props.rotaion : 0)) * element.props.width,
        xTo:
          element.props.x +
          Math.cos(element.props.rotaion ? element.props.rotaion : 0) * element.props.width,
        yFrom: element.props.y,
        yTo: element.props.y + element.props.height,
      }
    },
  },
  ellipse: {
    toolLayer: EllipseToolLayer,
    transformerLayer: EllipseTransformerLayer,
    transformerUI: EllipseTransformerUI,
    toolIcon: <Icon icon={Icons.faEllipsis} />,
    canvasShapeType: 'Ellipse',
    getAabb(element, pixelsPerCm) {
      return {
        xFrom: element.props.x - (1 - Math.cos(element.props.rotaion)) * element.props.radiusX,
        xTo: element.props.x + Math.cos(element.props.rotaion) * element.props.radiusX,
        yFrom: element.props.y,
        yTo: element.props.y + 2 * element.props.radiusY,
      }
    },
  },
  plant: {
    toolLayer: PlantingToolLayer,
    transformerLayer: RectTransformerLayer,
    transformerUI: undefined,
    toolIcon: '',
    canvasShapeType: 'Image',
    toolCursor: `url("${sproutIconUrl}") 10 26, auto`,
    getAabb(element, pixelsPerCm) {
      return {
        xFrom: element.props.x - 10,
        xTo: element.props.x + 10,
        yFrom: element.props.y - 10,
        yTo: element.props.y + 10,
      }
    },
  },
  image: {
    toolLayer: ImageToolLayer,
    transformerLayer: RectTransformerLayer,
    transformerUI: RectTransformerUI,
    toolIcon: <Icon icon={Icons.faSquare} />,
    canvasShapeType: 'Image',
    getAabb(element, pixelsPerCm) {
      return {
        xFrom:
          element.props.x -
          (1 - Math.cos(element.props.rotaion ? element.props.rotaion : 0)) * element.props.width,
        xTo:
          element.props.x +
          Math.cos(element.props.rotaion ? element.props.rotaion : 0) * element.props.width,
        yFrom: element.props.y,
        yTo: element.props.y + element.props.height,
      }
    },
  },
}
